import React, { useEffect, useMemo, useState, useRef } from"react";
import { useParams, useNavigate } from"react-router-dom";
import { useTenant } from"../Context/TenantContext";
import Editor from"@monaco-editor/react";
import axios from"axios";
import API_BASE_URL from"../Config/config";
import ChatBot from"../components/SkillGenBot/ChatBot";
import { hasFullAccess } from"../utils/paymentStatus";

// ✅ Utility to clean input before sending to backend
const sanitizeInput = (val) => {
 if (typeof val ==="object") return JSON.stringify(val);
 return String(val ||"");
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const normalizeOutput = (v) => {
 try {
 const num = parseFloat(v);
 if (!isNaN(num)) return num.toFixed(2);
 } catch {}
 return String(v).trim();
};

const LEVEL_ALIASES = {
 advance: ["Advance","Advanced"],
 advanced: ["Advanced","Advance"],
 basic: ["Basic"],
 intermediate: ["Intermediate"],
 expert: ["Expert"],
};

const SolveCodingQuestionPage = () => {
  const { language, level, questionId } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [question, setQuestion] = useState(null);
 const [code, setCode] = useState("");
 const [input, setInput] = useState("");
 const [output, setOutput] = useState("");
 const [isPremium, setIsPremium] = useState(false);
 const [hasAccess, setHasAccess] = useState(false);
 const [checkingAccess, setCheckingAccess] = useState(false);
 const [showChat, setShowChat] = useState(false);
 const chatBotRef = useRef(null);

 const normalizedLanguage = language.replace(/-/g,"").toLowerCase();
 const levelCandidates = useMemo(() => {
 const key = level.toLowerCase();
 return LEVEL_ALIASES[key] || [capitalize(key)];
 }, [level]);
 const [effectiveLevel, setEffectiveLevel] = useState(null);

  const pistonLangMap = {
    python: "python",
    c: "c",
    cpp: "cpp",
    java: "java",
    javascript: "javascript",
    nodejs: "javascript",
    html: "html",
    css: "css",
    reactjs: "javascript",
    sql: "mysql",
    postgresql: "postgresql",
    csharp: "csharp",
    php: "php",
    ruby: "ruby",
    swift: "swift",
    kotlin: "kotlin",
    go: "go",
    rust: "rust"
  };

 const requiresPremium = useMemo(() => {
 const levelLower = level.toLowerCase();
 return levelLower ==="advanced" || levelLower ==="advance" || levelLower ==="expert";
 }, [level]);

  // Check payment status BEFORE loading question
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      // BYPASS FOR TENANT
      // Grant full access if:
      // 1. We are currently in a tenant context (resolved from hostname)
      // 2. User object explicitly has tenant property
      if ((tenant || user.tenant) && user.role === 'student') {
        setHasAccess(true);
        setIsPremium(true);
        return;
      }

      if (tenant) {
        setHasAccess(true);
        setIsPremium(true);
        return;
      }

      if (!requiresPremium) {
        setHasAccess(true);
        return;
      }

      const userId = user?.userId || user?.user_id || "";

 if (!userId || userId ==="UnknownUser") {
 if (!cancelled) {
 setHasAccess(false);
 alert("Please log in to access premium challenges.");
 navigate("/skillgenpayments");
 }
 return;
 }

 try {
 setCheckingAccess(true);
 const allowed = await hasFullAccess(userId);
 if (!cancelled) {
 setIsPremium(allowed);
 setHasAccess(allowed);
 if (!allowed) {
 alert("Premium subscription required to access Advanced and Expert challenges.");
 navigate("/skillgenpayments");
 }
 }
 } catch (err) {
 console.error("⚠️ Failed to verify premium status:", err);
 if (!cancelled) {
 setHasAccess(false);
 navigate("/skillgenpayments");
 }
 } finally {
 if (!cancelled) setCheckingAccess(false);
 }
 })();
 return () => { cancelled = true; };
 }, [requiresPremium, navigate]);

 useEffect(() => {
 if (!hasAccess && requiresPremium) return; // Don't load if blocked
 
 const fetchQuestion = async () => {
 for (const lvl of levelCandidates) {
 try {
 const res = await fetch(
 `${API_BASE_URL}/practices/${normalizedLanguage}/${lvl}/id/${questionId}`
 );
 if (!res.ok) throw new Error(`HTTP ${res.status}`);
 const data = await res.json();
 setQuestion(data);
 setCode(data?.starter_code ||"");
 setOutput("");
 setEffectiveLevel(lvl);
 return;
 } catch (error) {
 console.error("Error fetching question:", error);
 }
 }
 alert("Could not load this challenge. Please pick another topic or level.");
 };

 fetchQuestion();
 }, [normalizedLanguage, levelCandidates, questionId, hasAccess, requiresPremium]);

 const [isRunning, setIsRunning] = useState(false);

 const runCode = async () => {
 // Prevent multiple simultaneous executions
 if (isRunning) return;
 
 setIsRunning(true);
 setOutput(""); // Clear previous output
 
 if (["html","css","reactjs","javascript"].includes(normalizedLanguage)) {
 setOutput("Preview below ⬇️");
 setIsRunning(false);
 return;
 }

 if (normalizedLanguage ==="postgresql") {
 try {
 const sqlRes = await axios.post(`${API_BASE_URL}/api/execute`, {
 language:"postgresql",
 code,
 input: sanitizeInput(input),
 });
 const sqlOutput =
 sqlRes.data.output?.trim() ||
 sqlRes.data.stderr?.trim() ||
"Query executed successfully.";
 setOutput(sqlOutput);
 } catch (err) {
 setOutput("Execution failed:" + (err.response?.data?.error || err.message));
 } finally {
 setIsRunning(false);
 }
 return;
 }

 if (!question?.test_cases || question.test_cases.length === 0) {
 setOutput("No test cases found.");
 setIsRunning(false);
 return;
 }

 const lang = pistonLangMap[normalizedLanguage] ||"python";

      try {
        const results = [];
        let sampleResultBlock = "";

        if (question.sample_input) {
          const sampleRes = await axios.post(`${API_BASE_URL}/api/execute`, {
            language: lang,
            code,
            input: sanitizeInput(question.sample_input),
          });

          const yourSampleOutput =
            sampleRes.data.output?.trim() ||
            sampleRes.data.stderr?.trim() ||
            "No output";

          const actual = normalizeOutput(yourSampleOutput);
          const expected = normalizeOutput(question.sample_output);
          const passed = actual === expected;

          sampleResultBlock += `📥 Sample Input:\n${question.sample_input}\n\n`;
          sampleResultBlock += `📤 Expected Output:\n${question.sample_output}\n\n`;
          sampleResultBlock += `🧪 Your Output:\n${yourSampleOutput}\n\n`;
          sampleResultBlock += `Result: ${passed ? "✅ Passed" : "❌ Failed"}\n\n---\n`;
        }

        for (let i = 0; i < question.test_cases.length; i++) {
          const testCase = question.test_cases[i];
          const res = await axios.post(`${API_BASE_URL}/api/execute`, {
            language: lang,
            code,
            input: sanitizeInput(testCase.input),
          });

          const actualRaw =
            res.data.output?.trim() || res.data.stderr?.trim() || "No output";
          const expectedRaw = testCase.expected_output?.trim();

          const actual = normalizeOutput(actualRaw);
          const expected = normalizeOutput(expectedRaw);
          const passed = actual === expected;

          results.push({
            index: i + 1,
            input: testCase.input,
            expected: expectedRaw,
            actual: actualRaw,
            passed,
            hidden: testCase.hidden || false,
          });
        }

        const formatted = [];
        formatted.push("🧪 Test Case Results:\n");

        results.forEach((t) => {
          formatted.push(
            `🧪 Test Case ${t.index} (${t.hidden ? "Hidden" : "Visible"}):
Input: ${t.input}
Expected Output: ${t.expected}
Your Output: ${t.actual}
Result: ${t.passed ? "✅ Passed" : "❌ Failed"}\n`
          );
        });

        setOutput(sampleResultBlock + formatted.join("\n"));
      } catch (err) {
        console.error("Execution error:", err.message);
        const errMsg = err.response?.data?.error || err.message;
        // Check if it's the specific whitelist restriction message from backend or Piston
        if (errMsg.toLowerCase().includes("whitelist-only") || errMsg.toLowerCase().includes("restricted") || errMsg.toLowerCase().includes("contact support")) {
          setOutput("Execution failed: The Public Compiler API is currently restricted (whitelist-only). Please ensure the dedicated Piston instance is correctly configured.");
        } else {
          setOutput("Execution failed: " + errMsg);
        }
      } finally {
        setIsRunning(false);
      }
 };

 if (requiresPremium && checkingAccess) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex items-center justify-center p-10">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
 <p className="text-primary font-bold uppercase tracking-widest text-sm">Verifying premium access...</p>
 </div>
 </div>
 );
 }

 if (requiresPremium && !hasAccess) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex flex-col items-center justify-center p-10 text-center">
 <div className="bg-bgSection p-12 rounded-[32px] border border-primary/30 shadow-2xl max-w-md w-full relative overflow-hidden">
 <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl"></div>
 <div className="text-6xl mb-6">🔒</div>
 <h2 className="text-3xl font-extrabold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4">Premium Required</h2>
 <p className="text-lg text-muted mb-8 leading-relaxed">
 Advanced & Expert challenges are available for SkillGenie Premium members. Unlock your full potential today!
 </p>
 <button
 onClick={() => navigate("/skillgenpayments")}
 className="btn-primary-gradient w-full py-4 text-lg shadow-xl"
 >
 Upgrade to Premium
 </button>
 <button
 onClick={() => navigate(-1)}
 className="mt-6 text-muted hover:text-primary text-sm font-bold transition-all block mx-auto"
 >
 ← Go Back
 </button>
 </div>
 </div>
 );
 }

 if (!question) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex items-center justify-center p-10">
 <div className="text-center">
 <div className="animate-pulse text-primary font-bold uppercase tracking-widest">Loading challenge...</div>
 </div>
 </div>
 );
 }

 return (
 <div className="flex h-screen bg-bgMain text-body relative overflow-hidden font-sans">
 {/* Left Panel */}
 <div className="w-[40%] p-8 overflow-y-auto border-r border-accentGray/30 bg-bgSection shadow-2xl relative">
 <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
 <button
 onClick={() => navigate(-1)}
 className="mb-8 text-muted hover:text-primary flex items-center gap-2 font-bold transition-colors group"
 >
 <span className="w-8 h-8 rounded-full bg-bgMain flex items-center justify-center group-hover:bg-primary/20 transition-all">←</span>
 Back
 </button>
 
 <h2 className="text-3xl font-extrabold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-8 leading-tight">
 {question.title}
 </h2>
 
 <div className="space-y-6">
 <Section title="Description" content={question.description} />
 <Section title="Explanation" content={question.explanation} />
 <Section title="Sample Input" content={question.sample_input} isCode />
 <Section title="Sample Output" content={question.sample_output} isCode />
 </div>
 </div>

 {/* Right Panel */}
 <div className="w-[60%] flex flex-col p-8 bg-bgMain overflow-auto relative">
 <div className="flex items-center justify-between mb-6">
 <h2 className="text-xl font-bold text-primary uppercase tracking-widest flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
 Code Editor
 </h2>
 <button
 onClick={runCode}
 disabled={isRunning}
 className={`btn-primary-gradient px-10 py-3 rounded-xl shadow-xl transition-all ${isRunning ?"opacity-50 cursor-not-allowed" :"hover:scale-105 active:scale-95"}`}
 >
 {isRunning ?"Running..." :"▶ Run Code"}
 </button>
 </div>

 <div className="flex-1 rounded-2xl overflow-hidden border border-accentGray/50 shadow-2xl min-h-[350px]">
 <Editor
 height="100%"
 language={pistonLangMap[normalizedLanguage] ||"python"}
 value={code}
 onChange={(value) => setCode(value)}
 theme="vs-dark"
 options={{
 fontSize: 16,
 minimap: { enabled: false },
 padding: { top: 20 },
 fontFamily:"'JetBrains Mono', monospace",
 theme: 'vs-dark',
 automaticLayout: true,
 }}
 />
 </div>

 <div className="mt-6 grid grid-cols-1 gap-4">
 <div>
 <label className="block text-muted text-xs font-bold uppercase tracking-widest mb-2 ml-1">Custom Input (Stdin)</label>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter input here..."
                className="w-full h-24 p-4 bg-black text-[#22c55e] font-mono text-sm border border-accentGray/50 rounded-2xl focus:border-primary outline-none transition-all resize-none shadow-inner font-bold"
              />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2 ml-1">
              <label className="text-muted text-xs font-bold uppercase tracking-widest">Execution Output</label>
              <button onClick={() => setOutput("")} className="text-[10px] text-muted hover:text-white font-bold transition-colors">CLEAR</button>
            </div>
            <pre className="w-full h-48 p-6 bg-black text-[#22c55e] font-mono text-sm border border-accentGray/50 rounded-2xl overflow-auto shadow-2xl font-bold">
              {output || <span className="text-muted italic">Output will appear here after running your code...</span>}
            </pre>
          </div>
 </div>

 {["html","css","reactjs"].includes(normalizedLanguage) && (
 <div className="mt-8 pt-8 border-t border-accentGray/30">
 <h3 className="text-primary text-xs font-bold uppercase tracking-widest mb-4 ml-1">Live Preview</h3>
 <div className="rounded-2xl overflow-hidden border-2 border-primary/30 shadow-2xl bg-white h-[400px]">
 <iframe
 title="Preview"
 sandbox="allow-scripts"
 srcDoc={
 normalizedLanguage ==="reactjs" 
 ? `<html><head><script crossorigin src="https://unpkg.com/react@18/umd/react.development.js"></script><script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script><script src="https://unpkg.com/@babel/standalone/babel.min.js"></script></head><body style="margin:0;padding:20px;font-family:sans-serif;"><div id="root"></div><script type="text/babel">${code}</script></body></html>`
 : normalizedLanguage ==="html" ? code : `<style>${code}</style>`
 }
 className="w-full h-full"
 />
 </div>
 </div>
 )}
 </div>

 {/* ChatBot Floating Button - HIDDEN */}
 {false && (
 <div style={{ position:"fixed", bottom:"32px", right:"32px", zIndex: 9999 }}>
 {showChat ? (
 <div style={{ position:"relative" }}>
 <button
 style={{
 position:"absolute",
 top: -10,
 right: -10,
 background:"transparent",
 border:"none",
 color:"var(--primary-blue, #4F46E5)",
 fontSize: 22,
 cursor:"pointer",
 zIndex: 10,
 }}
 onClick={() => setShowChat(false)}
 title="Close"
 >
 ×
 </button>
 <ChatBot ref={chatBotRef} />
 </div>
      ) : (
        <button
          style={{
            background:"var(--primary-blue)",
            color:"#fff",
            border:"none",
            borderRadius:"50%",
            width:"60px",
            height:"60px",
            boxShadow:"0 10px 20px rgba(43, 176, 230, 0.3)",
            cursor:"pointer",
            fontSize:"2rem",
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            transition:"all 0.3s"
          }}
          onMouseEnter={(e) => {
            e.target.style.transform ="scale(1.1)";
            e.target.style.background ="var(--secondary-blue)";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform ="scale(1)";
            e.target.style.background ="var(--primary-blue)";
          }}
          onClick={() => setShowChat(true)}
          title="Chat with SkillGenBot"
        >
          💬
        </button>
      )}
 </div>
 )}
 </div>
 );
};

const Section = ({ title, content, isCode = false }) => (
  <div
    style={{
      background:"var(--bg-section)",
      padding:"20px",
      borderRadius:"16px",
      boxShadow:"0 10px 30px rgba(31, 138, 203, 0.08)",
      border:"1px solid var(--accent-gray)",
      color:"var(--text-body)",
      fontSize:"15px",
      lineHeight:"1.6",
      marginBottom:"20px",
      whiteSpace: isCode ?"pre-wrap" :"normal",
      fontFamily: isCode ?"'JetBrains Mono', monospace" :"inherit",
    }}
  >
    <strong style={{ color:"var(--primary-blue)", fontSize:"12px", textTransform:"uppercase", letterSpacing:"1px", display:"block", marginBottom:"8px" }}>{title}</strong>
    <div style={{ color: isCode ?"var(--text-heading)" :"var(--text-body)" }}>{content}</div>
  </div>
);

export default SolveCodingQuestionPage;




